// Content Translation service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (content_translation_posting_params)
//   views      app/views/job_postings/steps/content_translation/_one.._two.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (content_translation__one..two)
//   cost       app/models/job_postings/content_translation_posting.rb (#cost)
//   parity     tasks/post-a-project/parity/enhance-text.md §4
//
// NO AI consent (none whitelisted / none in schema). Subtitle is optional and
// category is REQUIRED (design missed both). language drives the rate:
// German $3.55 / Spanish $3.19 per 100 words.

import {
  WRITING_NICHES,
  TRANSLATION_LANGUAGES,
  TRANSLATION_CATEGORIES,
  RATES,
} from "../options"

// string[] -> [{ value, label }] for select fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

// Design option descriptions (service-config.js). Restored per the Plumbline
// Phase 3 translation appendix (design owns option copy).
const LANGUAGE_DESCRIPTIONS = {
  German: "Native German translation that keeps your meaning and tone intact.",
  Spanish: "Native Spanish translation adapted for your target audience.",
}

// TRANSLATION_LANGUAGES radio options with the design price text "$X.XX / 100 words".
// Server-traced via ctx.rates.translator_cost (ruling C2 / tuw-app:P-001), with
// the per-language constant rate as the no-live-PriceList fallback.
const languageOptions = (ctx = {}) =>
  TRANSLATION_LANGUAGES.map((l) => {
    // Per-key: a null server leaf for this language falls back to the per-language
    // constant rate (= RATES.TRANSLATOR_COST[l.value]), never to 0 (tuw-app:P-001).
    const rate = ctx.rates?.translator_cost?.[l.value] ?? l.rate
    return {
      value: l.value,
      label: l.label,
      price: `$${rate.toFixed(2)} / 100 words`,
      sub: LANGUAGE_DESCRIPTIONS[l.value],
    }
  })

export const contentTranslation = {
  key: "content_translation",
  postingKey: "content_translation_posting",
  name: "Content Translation",

  steps: () => [
    // Step 1 — Target language (content_translation__one).
    {
      id: "language",
      title: "Target language",
      sub: "Which language are we translating into?",
      fields: [
        {
          type: "radio",
          key: "language",
          required: true,
          options: (v = {}, ctx = {}) => languageOptions(ctx),
        },
      ],
    },

    // Step 2 — Source details (content_translation__two).
    {
      id: "details",
      title: "Source details",
      sub: "Tell us about the content to translate.",
      fields: [
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        { type: "text", key: "author_name", label: "Author name", required: true, placeholder: "As it should appear" },
        {
          type: "wordcount",
          key: "word_count",
          label: "Word count",
          help: "Minimum 1,000, in increments of 500.",
          required: true,
          min: 1000,
          step: 500,
        },
        {
          type: "select",
          key: "category",
          label: "Category",
          required: true,
          blankLabel: "Select category",
          options: asOptions(TRANSLATION_CATEGORIES),
        },
        {
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: asOptions([...WRITING_NICHES].sort()),
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby (content_translation_posting.rb#cost):
  //   (word_count / 100) * translation_cost   (INTEGER division -> floor)
  // translation_cost = TRANSLATOR_COST[language] (German 3.55 / Spanish 3.19).
  estimate: (values = {}, ctx = {}) => {
    const wc = Number(values.word_count) || 0
    const rate = ctx.rates?.translator_cost?.[values.language] ?? RATES.TRANSLATOR_COST[values.language] ?? 0
    const lines = []

    if (wc > 0 && rate > 0) {
      const units = Math.floor(wc / 100)
      lines.push({
        label: `Translation (${values.language}, ${wc.toLocaleString()} words)`,
        amount: money(units * rate),
      })
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // content_translation_posting = [word_count, language]. Null until both are set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (wc <= 0 || !values.language) return null

    return {
      postingType: "content_translation_posting",
      wordCount: wc,
      language: values.language,
    }
  },
}
